import { differenceInMilliseconds, parseISO } from "date-fns"
import { ChevronRight, CircleAlert, CircleCheck, Plug, PlugZap, Sparkles, Wrench } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { ToolCallRow } from "@/api/generated/ToolCallRow"
import { CodeBlock } from "@/components/app/code-block"
import { Markdown } from "@/components/app/markdown"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Spinner } from "@/components/ui/spinner"
import { formatDuration, prettyJson } from "@/features/runs/format"
import type { LiveToolCall, RunTimelineEntry } from "@/features/runs/run-stream"
import { cn } from "@/lib/utils"

export interface ToolCallView {
  id: string
  name: string
  label: string
  kind: string
  args: unknown
  result?: unknown
  isError?: boolean
  durationMs?: number
  status: "running" | "done" | "error"
}

export function fromToolCallRow(r: ToolCallRow): ToolCallView {
  const duration =
    r.completed_at != null
      ? Math.max(0, differenceInMilliseconds(parseISO(r.completed_at), parseISO(r.started_at)))
      : undefined
  return {
    id: r.tool_call_id || String(r.id),
    name: r.tool_name,
    label: r.label || r.tool_name,
    kind: r.kind,
    args: r.arguments,
    result: r.result ?? undefined,
    isError: r.is_error,
    durationMs: duration,
    status: r.is_error ? "error" : r.completed_at ? "done" : "running",
  }
}

/** Persisted calls first, then live ones the database does not know yet (merged by id). */
export function mergeToolCalls(persisted: ToolCallView[], live: LiveToolCall[]): ToolCallView[] {
  const out = persisted.slice()
  for (const l of live) {
    const i = out.findIndex((p) => p.id === l.id)
    if (i === -1) out.push(l)
    else if (out[i]!.status === "running") out[i] = { ...out[i]!, ...l, label: out[i]!.label || l.label }
  }
  return out
}

const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  builtin: Wrench,
  tool: Wrench,
  mcp: Plug,
  mcp_mock: Plug,
  mcp_load: PlugZap,
  skill: Sparkles,
}

function ToolCallItem({ call, defaultOpen }: { call: ToolCallView; defaultOpen?: boolean }) {
  const { t } = useTranslation()
  const Icon = KIND_ICON[call.kind] ?? Wrench
  const [open, setOpen] = useState<boolean>()
  const args = prettyJson(call.args)
  const hasArgs = !!args && args !== "{}"
  const result = call.result === undefined ? "" : prettyJson(call.result)
  return (
    <li className="relative pl-7">
      <span
        aria-hidden
        className={cn(
          "bg-background absolute top-2 left-0 flex size-5 items-center justify-center rounded-full border",
          call.status === "error" && "border-destructive/40 text-destructive",
          call.status === "done" && "text-muted-foreground",
          call.status === "running" && "border-info/40 text-info",
        )}
      >
        <Icon className="size-3" />
      </span>
      <Collapsible open={open ?? !!defaultOpen} onOpenChange={setOpen}>
        <CollapsibleTrigger className="group hover:bg-muted/50 focus-visible:ring-ring/50 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none focus-visible:ring-2">
          <ChevronRight className="text-muted-foreground size-3.5 shrink-0 transition-transform group-data-[panel-open]:rotate-90" />
          <span className="min-w-0 flex-1 truncate font-mono text-[0.8125rem]">
            {call.label && call.label !== call.name ? (
              <>
                <span className="font-sans font-medium">{call.label}</span>{" "}
                <span className="text-muted-foreground">{call.name}</span>
              </>
            ) : (
              call.name || call.label
            )}
          </span>
          <span className="text-muted-foreground flex shrink-0 items-center gap-1.5 text-xs tabular-nums">
            {call.durationMs != null && call.status !== "running" && formatDuration(call.durationMs)}
            {call.status === "running" ? (
              <Spinner className="text-info size-3.5" />
            ) : call.status === "error" ? (
              <CircleAlert className="text-destructive size-3.5" />
            ) : (
              <CircleCheck className="text-success size-3.5" />
            )}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 px-2 pt-1 pb-2">
          {hasArgs && (
            <CodeBlock code={args} title={t("insights.runs.tools.arguments")} maxHeight={260} wrap />
          )}
          {call.status === "running" ? (
            <p className="text-muted-foreground flex items-center gap-2 text-xs">
              <Spinner className="size-3" /> {t("insights.runs.tools.running")}
            </p>
          ) : result ? (
            <CodeBlock
              code={result}
              title={call.isError ? t("insights.runs.tools.error") : t("insights.runs.tools.result")}
              maxHeight={360}
              wrap
              className={cn(call.isError && "border-destructive/30 bg-destructive/[0.04]")}
            />
          ) : (
            <p className="text-muted-foreground text-xs">{t("insights.runs.tools.noResult")}</p>
          )}
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

/** Vertical timeline of tool calls with expandable arguments/results. */
export function ToolCallTimeline({ calls, className }: { calls: ToolCallView[]; className?: string }) {
  if (!calls.length) return null
  return (
    <ol
      className={cn(
        "relative space-y-0.5 before:absolute before:top-3 before:bottom-3 before:left-2.5 before:w-px before:bg-border",
        className,
      )}
    >
      {calls.map((c) => (
        <ToolCallItem key={c.id} call={c} defaultOpen={c.status === "error"} />
      ))}
    </ol>
  )
}

/**
 * A live run in order: text the model wrote before calling tools, interleaved
 * with those calls. Consecutive calls share one timeline; calls the timeline
 * does not mention (e.g. picked up from a snapshot) follow at the end.
 */
export function RunTimeline({ timeline, calls }: { timeline: RunTimelineEntry[]; calls: ToolCallView[] }) {
  const byId = new Map(calls.map((c) => [c.id, c]))
  const groups: ({ type: "text"; text: string } | { type: "tools"; calls: ToolCallView[] })[] = []
  const seen = new Set<string>()
  const pushCall = (c: ToolCallView) => {
    seen.add(c.id)
    const last = groups.at(-1)
    if (last?.type === "tools") last.calls.push(c)
    else groups.push({ type: "tools", calls: [c] })
  }
  for (const e of timeline) {
    if (e.type === "text") groups.push({ type: "text", text: e.text })
    else {
      const c = byId.get(e.id)
      if (c && !seen.has(c.id)) pushCall(c)
    }
  }
  for (const c of calls) if (!seen.has(c.id)) pushCall(c)
  return groups.map((g, i) =>
    g.type === "text" ? (
      <Markdown key={i} size="sm">
        {g.text}
      </Markdown>
    ) : (
      <ToolCallTimeline key={i} calls={g.calls} />
    ),
  )
}
